import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import en from './locales/en.json'
import es from './locales/es.json'

// bluaIQ i18n — Default: Spanish (es)
// Detects only from localStorage key 'bluaiq-lang'
// No navigator.language fallback — consistent across browsers

const LANG_KEY = 'bluaiq-lang'
const LEGACY_KEY = 'i18nextLng'
// Bump this whenever we need to force-reset every user's language
// preference. Used once to fix users whose browsers got stuck on 'en'
// from the pre-custom-key era of the app.
const RESET_FLAG = 'bluaiq-lang-reset-v1'

// Run BEFORE i18n.init() so the detector sees the correct localStorage state.
// This is synchronous and executes during `import './i18n'` in main.tsx,
// which runs before ReactDOM.createRoot().render(). By the time React mounts
// anything, localStorage is guaranteed to be in the right state.
if (typeof window !== 'undefined') {
  // One-time force-reset to Spanish for every existing user. This fixes
  // users whose `bluaiq-lang` got stuck on 'en' from earlier builds that
  // wrote navigator.language to the cache. Runs exactly once per browser.
  // Users who genuinely prefer English can re-select it via the sidebar
  // toggle after the reset.
  if (!localStorage.getItem(RESET_FLAG)) {
    localStorage.setItem(LANG_KEY, 'es')
    localStorage.setItem(RESET_FLAG, '1')
  }

  // First-time users (no RESET_FLAG yet runs above, but this is belt-and-
  // suspenders): make sure there is always a value in LANG_KEY, honoring
  // any legacy i18nextLng preference if one exists.
  if (!localStorage.getItem(LANG_KEY)) {
    const legacy = localStorage.getItem(LEGACY_KEY)
    localStorage.setItem(
      LANG_KEY,
      legacy === 'es' || legacy === 'en' ? legacy : 'es'
    )
  }

  // Clean up the legacy key to prevent DevTools confusion during debugging.
  // (Safe to remove on every load — we've already migrated any value we
  // cared about above.)
  localStorage.removeItem(LEGACY_KEY)
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: 'es',
    supportedLngs: ['en', 'es'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: LANG_KEY,
      caches: ['localStorage'],
    },
  })

// Sync <html lang> with i18n for CSS :lang() selectors, screen readers,
// and Chrome MCP verification that checks document.documentElement.lang.
if (typeof window !== 'undefined') {
  document.documentElement.lang = i18n.language || 'es'
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng
  })
  // Debug handle for DevTools and Chrome MCP verification.
  // @ts-expect-error attach debug helper
  window.__i18n = i18n
}

export default i18n
